import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, Modal, useWindowDimensions } from 'react-native';
import { NavigationTab, TAB_CONFIGS } from '../../types/navigation';
import { DebugView } from './DebugView';
import { FaqsModal } from '../FaqsModal';
import * as appConfig from '../../config/configManager';
import { useUserStore } from '../../contexts/StoreContext';
import { syncDirtyProfiles } from '../../utils/syncBeforeSignOut';

interface MoreViewProps {
  activeTab: NavigationTab;
  onTabChange: (tab: NavigationTab) => void;
  storage?: any;
  apiService?: any;
  contentService?: any;
  useContentStore?: any;
}

const isDevelopmentMode = (): boolean => {
  return typeof __DEV__ !== 'undefined' ? __DEV__ : (globalThis as any).process?.env?.NODE_ENV === 'development';
};

export const MoreView: React.FC<MoreViewProps> = ({ activeTab, onTabChange, storage, apiService, contentService, useContentStore }) => {
  const [showDebugView, setShowDebugView] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showFaqs, setShowFaqs] = useState(false);
  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth >= 700;
  const userStore = useUserStore();

  const handleSignOut = () => {
    setShowSignOutModal(true);
  };

  const confirmSignOut = () => {
    setShowSignOutModal(false);
    const doSignOut = async () => {
      // Sync any locally-modified profiles before clearing the session
      if (Platform.OS === 'web') {
        await syncDirtyProfiles();
      }
      if (userStore?.getState) {
        await userStore.getState().logout();
      }
      if (Platform.OS === 'web') {
        (globalThis as any).__webSignOut?.();
      }
    };
    doSignOut();
  };

  // Handle navigation to debug view
  const handleDebugTools = () => {
    setShowDebugView(true);
  };

  const handleBackFromDebug = () => {
    setShowDebugView(false);
  };

  // If showing debug view, render that view instead
  if (showDebugView) {
    return (
      <DebugView 
        onBack={handleBackFromDebug}
        storage={storage}
        apiService={apiService}
        contentService={contentService}
        useContentStore={useContentStore}
      />
    );
  }
  // Define all available tabs
  const allTabs: NavigationTab[] = [
    'ChildNav',
    'Milestones',
    'Concerns',
    'Well-Visit',
    'Guidance',
    'Actions',
    'Settings'
  ];

  const renderTabItem = (tab: NavigationTab, labelOverride?: string) => {
    const config = TAB_CONFIGS[tab];
    const isActive = activeTab === tab;
    const label = labelOverride ?? config.label;

    return (
      <TouchableOpacity
        key={tab}
        style={[styles.tabItem, isActive && styles.activeTabItem]}
        onPress={() => onTabChange(tab)}
        accessibilityLabel={`Navigate to ${label}`}
        accessibilityRole="button"
      >
        <View style={styles.tabContent}>
          <Text style={[styles.tabIcon, isActive && styles.activeTabIcon]}>
            {config.icon}
          </Text>
          <View style={styles.tabTextContainer}>
            <Text style={[styles.tabLabel, isActive && styles.activeTabLabel]}>
              {label}
            </Text>
            <Text style={styles.tabDescription}>
              {config.description}
            </Text>
          </View>
        </View>
        <Text style={[styles.tabArrow, isActive && styles.activeTabArrow]}>›</Text>
      </TouchableOpacity>
    );
  };

  const renderActionItem = (
    title: string,
    description: string,
    onPress: () => void,
    icon?: string,
    disabled?: boolean
  ) => (
    <TouchableOpacity 
      style={[styles.actionItem, disabled && styles.actionItemDisabled]} 
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
    >
      <View style={styles.actionContent}>
        {icon && <Text style={styles.actionIcon}>{icon}</Text>}
        <View style={styles.actionText}>
          <Text style={[styles.actionTitle, disabled && styles.actionTitleDisabled]}>{title}</Text>
          <Text style={[styles.actionDescription, disabled && styles.actionDescriptionDisabled]}>{description}</Text>
        </View>
      </View>
      <Text style={[styles.actionArrow, disabled && styles.actionArrowDisabled]}>›</Text>
    </TouchableOpacity>
  );

  const renderSection = (title: string, tabs: NavigationTab[], additionalItems?: React.ReactNode) => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {tabs.map(tab => renderTabItem(tab))}
      {additionalItems}
    </View>
  );

  // Group tabs by category
  const primaryTabs = ['ChildNav', 'Milestones', 'Concerns', 'Activities', 'Well-Visit', 'ManageProfiles', 'Actions'] as NavigationTab[];
  const resourceTabs = ['Guidance', 'ResourceList', 'Connections'] as NavigationTab[];
  const appTabs = ['Account', 'Settings'] as NavigationTab[];

  return (
    <View style={styles.backgroundContainer}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {/* Constraint wrapper */}
        <View style={{ maxWidth: appConfig.getTwoCardMaxWidth(), alignSelf: 'center', width: '100%', paddingHorizontal: appConfig.getCardMargin() }}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Text style={styles.title}>Navigation</Text>
              <Text style={styles.subtitle}>Choose where you'd like to go</Text>
            </View>
            {Platform.OS === 'web' && (
              <TouchableOpacity style={styles.headerSignOut} onPress={handleSignOut}>
                <Text style={styles.headerSignOutText}>🚪 Sign Out</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Responsive row: Primary | Resources+App */}
          <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, alignItems: isWide ? 'flex-start' : 'stretch' }}>

            {/* Primary card */}
            <View style={[styles.section, { flex: isWide ? 1 : undefined, marginHorizontal: 0, marginTop: 0 }]}>
              <Text style={styles.sectionTitle}>Primary</Text>
              {primaryTabs.map(tab => renderTabItem(tab, tab === 'Actions' ? 'Manage Actions' : undefined))}
            </View>

            {/* Combined Resources + App card */}
            <View style={{ flex: isWide ? 1 : undefined }}>
              <View style={[styles.section, { marginHorizontal: 0, marginTop: 0 }]}>
                <Text style={styles.sectionTitle}>Resources</Text>
                {resourceTabs.map(tab => renderTabItem(tab))}
              </View>
              <View style={[styles.section, { marginHorizontal: 0 }]}>
                <Text style={styles.sectionTitle}>App</Text>
                {appTabs.map(tab => renderTabItem(tab))}
                <TouchableOpacity
                  style={styles.tabItem}
                  onPress={() => setShowFaqs(true)}
                  accessibilityLabel="Help & FAQs"
                  accessibilityRole="button"
                >
                  <View style={styles.tabContent}>
                    <Text style={styles.tabIcon}>❓</Text>
                    <View style={styles.tabTextContainer}>
                      <Text style={styles.tabLabel}>FAQs</Text>
                      <Text style={styles.tabDescription}>Get answers to common questions</Text>
                    </View>
                  </View>
                  <Text style={styles.tabArrow}>›</Text>
                </TouchableOpacity>
                {isDevelopmentMode() ? renderActionItem(
                  'Development Tools',
                  'Advanced debugging and testing utilities',
                  handleDebugTools,
                  '🔧'
                ) : null}

              </View>
            </View>

          </View>

          <View style={styles.footer}>
            <Text style={styles.footerText}>
              Tap any item above to navigate to that section
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* Sign Out Confirmation Modal */}
      {Platform.OS === 'web' && (
        <Modal
          visible={showSignOutModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowSignOutModal(false)}
        >
          <View style={styles.signOutDialogBackdrop}>
            <View style={styles.signOutDialogCard}>
              <Text style={styles.signOutDialogTitle}>Sign Out</Text>
              <Text style={styles.signOutDialogMessage}>
                Are you sure you want to sign out? Your local data will be preserved.
              </Text>
              <View style={styles.signOutDialogButtons}>
                <TouchableOpacity
                  style={styles.signOutDialogBtnPrimary}
                  onPress={confirmSignOut}
                >
                  <Text style={styles.signOutDialogBtnPrimaryText}>Sign Out</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.signOutDialogBtnSecondary}
                  onPress={() => setShowSignOutModal(false)}
                >
                  <Text style={styles.signOutDialogBtnSecondaryText}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      )}

      <FaqsModal visible={showFaqs} onClose={() => setShowFaqs(false)} />
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    backgroundColor: '#3b82f6',
    paddingVertical: 16,
  },
  header: {
    backgroundColor: 'transparent',
    padding: 24,
    borderBottomWidth: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flex: 1,
  },
  headerSignOut: {
    paddingTop: 4,
    paddingLeft: 16,
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
  },
  headerSignOutText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#e2e8f0',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  section: {
    backgroundColor: '#ffffff',
    marginHorizontal: 16,
    marginTop: 16,
    paddingVertical: 8,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#7f8c8d',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#f8f9fa',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginHorizontal: -0.5,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  tabItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  activeTabItem: {
    backgroundColor: '#f0f8ff',
  },
  tabContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  tabIcon: {
    fontSize: 24,
    marginRight: 16,
  },
  activeTabIcon: {
    transform: [{ scale: 1.1 }],
  },
  tabTextContainer: {
    flex: 1,
  },
  tabLabel: {
    fontSize: 18,
    fontWeight: '500',
    color: '#2c3e50',
    marginBottom: 2,
  },
  activeTabLabel: {
    color: '#3498db',
    fontWeight: '600',
  },
  tabDescription: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  tabArrow: {
    fontSize: 24,
    color: '#bdc3c7',
    fontWeight: 'bold',
  },
  activeTabArrow: {
    color: '#3498db',
  },
  footer: {
    padding: 20,
    alignItems: 'center',
    marginBottom: 20,
  },
  footerText: {
    fontSize: 14,
    color: '#e2e8f0',
    textAlign: 'center',
    lineHeight: 20,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  actionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  actionIcon: {
    fontSize: 20,
    marginRight: 12,
    width: 24,
    textAlign: 'center',
  },
  actionText: {
    flex: 1,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2c3e50',
    marginBottom: 2,
  },
  actionDescription: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  actionArrow: {
    fontSize: 18,
    color: '#bdc3c7',
    fontWeight: 'bold',
  },
  actionItemDisabled: {
    opacity: 0.5,
  },
  actionTitleDisabled: {
    color: '#95a5a6',
  },
  actionDescriptionDisabled: {
    color: '#bdc3c7',
  },
  actionArrowDisabled: {
    color: '#ecf0f1',
  },

  // Sign Out confirmation dialog
  signOutDialogBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  signOutDialogCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  signOutDialogTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 10,
  },
  signOutDialogMessage: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
    marginBottom: 24,
  },
  signOutDialogButtons: {
    flexDirection: 'column',
    alignItems: 'stretch',
    marginTop: 4,
  },
  signOutDialogBtnSecondary: {
    paddingVertical: 12,
    marginHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#3498db',
    alignItems: 'center',
  },
  signOutDialogBtnSecondaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#3498db',
    textAlign: 'center',
  },
  signOutDialogBtnPrimary: {
    paddingVertical: 12,
    marginHorizontal: 8,
    marginBottom: 10,
    borderRadius: 8,
    backgroundColor: '#e74c3c',
    alignItems: 'center',
  },
  signOutDialogBtnPrimaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
});